import { Form, InputField } from 'components/Form';
import PropTypes from 'prop-types';
import * as z from 'zod';
import { useState } from 'react';
import { useAuth } from 'lib/auth';
import { EyeIcon, EyeOffIcon, PhoneIcon, LockClosedIcon } from '@heroicons/react/outline';

const schema = z.object({
  phoneCode: z.string().min(1, 'Required'),
  phone: z.string().min(1, 'Required'),
  password: z.string().min(1, 'Required'),
});

export const LoginForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [revealPass, setRevealPass] = useState(false);
  const { login } = useAuth();

  return (
    <div
      className="
      max-w-md mx-auto mt-16 p-8
      rounded-3xl bg-white/90 backdrop-blur-xl
      shadow-xl border border-white/40
      relative overflow-hidden
    "
    >
      {/* Green Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-transparent blur-2xl opacity-40"></div>

      <div className="relative z-10">
        <div className="text-sm mb-6 text-center text-gray-600">
          Fundo is currently in private beta. To request an account, email
          <span className="text-green-700 font-medium underline ml-1">farmer@fundo.org</span>
        </div>

        <h2 className="text-3xl font-extrabold text-gray-900 mb-6 text-center tracking-tight">
          Welcome Back to <span className="text-green-700">Fundo</span>
        </h2>

        <Form
          onSubmit={async (values) => {
            setLoading(true);

            const fullPhone = `${values.phoneCode}${values.phone}`.replace(/\s+/g, '');

            await login({
              phone: fullPhone,
              password: values.password,
            }).catch(() => {
              setLoading(false);
            });

            onSuccess();
          }}
          schema={schema}
        >
          {({ register, formState }) => (
            <>
              {/* PHONE INPUT */}
              <div className="mb-5">
                <label className="block text-gray-700 text-sm font-medium mb-1">Phone Number</label>

                <div
                  className="
                  flex items-center
                  bg-gray-50 border rounded-xl
                  overflow-hidden transition
                  focus-within:ring-2 focus-within:ring-green-600
                "
                >
                  <PhoneIcon className="h-5 w-5 text-green-600 ml-3" />

                  <select
                    className="bg-transparent px-2 py-3 border-r text-gray-700 outline-none"
                    {...register('phoneCode')}
                  >
                    <option value="+256">🇺🇬 +256</option>
                    <option value="+254">🇰🇪 +254</option>
                    <option value="+255">🇹🇿 +255</option>
                    <option value="+250">🇷🇼 +250</option>
                  </select>

                  <input
                    type="text"
                    placeholder="782692271"
                    className="flex-1 px-3 py-3 outline-none bg-transparent"
                    {...register('phone')}
                  />
                </div>

                {formState.errors['phone'] && (
                  <p className="text-red-500 text-xs mt-1">{formState.errors['phone'].message}</p>
                )}
              </div>

              {/* PASSWORD INPUT */}
              <div className="mb-5">
                <label className="block text-gray-700 text-sm font-medium mb-1">Password</label>

                <div
                  className="
                  relative bg-gray-50 border rounded-xl flex items-center
                  transition focus-within:ring-2 focus-within:ring-green-600
                "
                >
                  <LockClosedIcon className="h-5 w-5 text-green-600 ml-3" />

                  <input
                    type={revealPass ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="flex-1 px-3 py-3 outline-none bg-transparent"
                    {...register('password')}
                  />

                  <button
                    type="button"
                    onClick={() => setRevealPass(!revealPass)}
                    className="mr-3 text-gray-400 hover:text-gray-700 transition"
                  >
                    {revealPass ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {formState.errors['password'] && (
                  <p className="text-red-500 text-xs mt-1">
                    {formState.errors['password'].message}
                  </p>
                )}
              </div>

              {/* SUBMIT BUTTON */}
              <div className="mt-8">
                <button
                  disabled={loading}
                  type="submit"
                  className={`
                    w-full px-8 py-3 rounded-xl text-lg font-semibold text-white
                    bg-green-600 hover:bg-green-700
                    shadow-lg shadow-green-200
                    transition flex items-center justify-center
                    ${loading && 'opacity-70 cursor-not-allowed'}
                  `}
                >
                  {loading ? <span className="animate-pulse">Loading…</span> : 'Continue'}
                </button>
              </div>
            </>
          )}
        </Form>
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};
